import React, { useContext, useState } from "react";
import ReactDOM from "react-dom";
import { z } from "zod";
import { UserContext } from "../../contexts/UserContext";
import {
  LoginSchema,
  RecoverSchema,
  RegisterSchema,
} from "../../schemas/auth-schemas";
import LoginForm from "./LoginForm";
import RecoverForm from "./RecoverForm";
import RegisterForm from "./RegisterForm";
import { AUTH_METHODS } from "../../constants";
import "./tailwind.css";

// Icon components
const AccountIcon = ({ size = 20, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const CloseIcon = ({ size = 24, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const AlertIcon = ({ size = 20, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const ArrowBackIcon = ({ size = 16, color }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="12 19 5 12 12 5" />
  </svg>
);

const EmailLoginDialog = ({ open, onClose }) => {
  const [formType, setFormType] = useState("login");
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldFocus, setFieldFocus] = useState({
    username: false,
    email: false,
    password: false,
  });
  const [isDarkMode, setIsDarkMode] = useState(
    typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
  );
  const { checkAuth } = useContext(UserContext);

  // Detect dark mode changes
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => setIsDarkMode(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const colors = {
    primary: '#4682B4',
    background: isDarkMode ? '#1C1C1E' : '#FFFFFF',
    card: isDarkMode ? '#2C2C2E' : '#F5F5F5',
    text: isDarkMode ? '#F0F0F0' : '#0E0E0E',
    secondary: isDarkMode ? '#8E8E93' : '#6E6E73',
    error: '#FF3B30',
    success: '#34C759',
    border: isDarkMode ? '#3A3A3C' : '#E5E5EA',
  };

  const handleSuccess = () => {
    // Call checkAuth and wait a moment for state to update before closing
    checkAuth();

    // Small delay to ensure state updates propagate
    setTimeout(() => {
      onClose();
      // Force a re-render by reloading if on login/register pages
      if (window.location.pathname === '/login' || window.location.pathname === '/register') {
        window.location.href = '/';
      }
    }, 100);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setErrors({});
    
    const formData = new FormData(event.currentTarget);
    const formJson = Object.fromEntries(formData.entries());

    const schemaMap = {
      login: LoginSchema,
      register: RegisterSchema,
      recover: RecoverSchema,
    };

    // Use REACT_APP_API_URL, but always prefer localhost when running locally
    // React env vars are only available at build time, so we check hostname as fallback
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const apiBaseUrl = isLocal
      ? (process.env.REACT_APP_API_URL || 'http://localhost:3001')
      : (process.env.REACT_APP_API_URL || 'https://crossovergames-8b4eb8e6663f.herokuapp.com');
    
    // Force localhost if we're on localhost (override env var if needed for dev)
    const finalApiUrl = isLocal && !apiBaseUrl.includes('localhost') 
      ? 'http://localhost:3001' 
      : apiBaseUrl;
    
    const urlMap = {
      login: `${finalApiUrl}/api/auth/login`,
      register: `${finalApiUrl}/api/auth/register`,
      recover: `${finalApiUrl}/api/auth/forgot-password`,
    };

    // Declare timeoutId and controller outside try block for catch block access
    let timeoutId = null;
    const controller = new AbortController();

    try {
      const schema = schemaMap[formType];
      schema.parse(formJson);

      const url = urlMap[formType];
      let payload = { ...formJson };
      if (formType === 'register' && payload.confirmPassword) {
        delete payload.confirmPassword;
      }
      
      // Set timeout to prevent hanging
      timeoutId = setTimeout(() => {
        controller.abort();
      }, 10000); // 10 second timeout
      
      console.log('Making request to:', url);
      console.log('API Base URL:', finalApiUrl, '(isLocal:', isLocal, ')');
      const startTime = Date.now();
      
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      
      const endTime = Date.now();
      console.log(`Request took ${endTime - startTime}ms`);
      
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      let data;
      try {
        data = await response.json();
      } catch (e) {
        const text = await response.text();
        throw new Error(`Server error: ${text || response.statusText}`);
      }
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Invalid username or password');
        }
        if (response.status === 404) {
          throw new Error('User not found');
        }
        if (response.status === 400) {
          throw new Error(data?.message || data?.error || 'Invalid request. Please check your input.');
        }
        if (response.status === 409) {
          throw new Error(data?.message || data?.error || 'User already exists');
        }
        throw new Error(data?.message || data?.error || `HTTP error! status: ${response.status}`);
      }
      
      if (data.error) {
        setErrors({
          general: data.message || "An error occurred while processing your request.",
        });
        setIsSubmitting(false);
      } else {
        if (data.token && data.user) {
          console.log('Login successful! User:', data.user.username);
          // Store authentication data (matching backend response format)
          localStorage.setItem('AUTH_TOKEN', data.token);
          localStorage.setItem('authToken', data.token); // Also store as 'authToken' for compatibility
          localStorage.setItem('user_data', JSON.stringify(data.user));
          // Store userId and username separately for easy access
          localStorage.setItem('userId', String(data.user.id));
          localStorage.setItem('username', data.user.username);
          localStorage.setItem('is_guest', 'false');

          // Show success message
          setErrors({ general: '' });
        }
        handleSuccess();
        setIsSubmitting(false);
      }
    } catch (error) {
      setIsSubmitting(false);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      
      console.error('Registration error:', error);
      
      if (error.name === 'AbortError') {
        setErrors({ general: 'Request timed out after 10 seconds. Please check your connection and ensure the backend is running on port 3001.' });
      } else if (error instanceof z.ZodError) {
        const fieldErrors = error.flatten().fieldErrors;
        setErrors(fieldErrors);
        } else {
          const errorMessage = error.message || 'An error occurred. Please try again.';
          if (errorMessage.includes('fetch') || errorMessage.includes('Network') || errorMessage.includes('Failed to fetch') || errorMessage.includes('AbortError')) {
            setErrors({ general: `Network error: ${errorMessage}. Please check your connection and ensure the backend is running on port 3001 at ${finalApiUrl}` });
        } else {
          setErrors({ general: errorMessage });
        }
      }
    }
  };

  const handleProviderClick = (authUrl) => {
    window.location.href = authUrl;
  };

  if (!open) return null;

  const modalContent = (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(4px)',
        zIndex: 99999,
        isolation: 'isolate',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="relative rounded-2xl shadow-2xl w-full flex flex-col"
        style={{
          backgroundColor: colors.background,
          maxWidth: '440px',
          minWidth: '320px',
          width: '100%',
          maxHeight: '85vh',
          height: formType === 'register' ? '600px' : formType === 'login' ? 'auto' : 'auto',
          zIndex: 100000,
          isolation: 'isolate',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button - Fixed Position */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 p-2 rounded-full hover:opacity-70 transition-opacity"
          style={{ 
            color: colors.secondary,
            backgroundColor: colors.background + 'CC',
            backdropFilter: 'blur(4px)',
          }}
        >
          <CloseIcon size={24} color={colors.secondary} />
        </button>

        {/* Scrollable Content */}
        <div 
          className="overflow-y-auto"
          style={{ 
            maxHeight: formType === 'register' ? '600px' : '85vh',
            height: formType === 'register' ? '600px' : 'auto',
          }}
        >

        {/* Header */}
        <div className="pt-8 pb-4 px-6 sm:px-8 text-center">
          <h1 
            className="text-xl sm:text-2xl font-bold mb-1"
            style={{ color: colors.text }}
          >
            {formType === "login" && "Sign In"}
            {formType === "register" && "Create Account"}
            {formType === "recover" && "Reset Password"}
          </h1>
          {formType === "recover" && (
            <p 
              className="text-sm"
              style={{ color: colors.secondary }}
            >
              We'll send a verification code to your email
            </p>
          )}
        </div>

        {/* Segmented Control */}
        {formType !== "recover" && (
          <div className="px-6 sm:px-8 mb-4">
            <div 
              className="flex rounded-xl p-1 max-w-xs mx-auto"
              style={{ backgroundColor: colors.card }}
            >
              <button
                type="button"
                onClick={() => setFormType("register")}
                className="flex-1 py-2.5 px-4 sm:px-6 rounded-lg text-sm font-semibold transition-all hover:opacity-90"
                style={{
                  backgroundColor: formType === "register" ? colors.primary : 'transparent',
                  color: formType === "register" ? '#FFFFFF' : colors.secondary,
                }}
              >
                Create account
              </button>
              <button
                type="button"
                onClick={() => setFormType("login")}
                className="flex-1 py-2.5 px-4 sm:px-6 rounded-lg text-sm font-semibold transition-all hover:opacity-90"
                style={{
                  backgroundColor: formType === "login" ? colors.primary : 'transparent',
                  color: formType === "login" ? '#FFFFFF' : colors.secondary,
                }}
              >
                Sign in
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="px-6 sm:px-8 pb-8">
          {/* Error Message */}
          {errors.general && (
            <div 
              className="mb-4 p-3 rounded-lg flex items-center gap-2"
              style={{ 
                backgroundColor: colors.error + '20',
                border: `1px solid ${colors.error}`,
                color: colors.error,
              }}
            >
              <AlertIcon size={20} color={colors.error} />
              <span className="text-sm">{errors.general}</span>
            </div>
          )}

          {/* Form Fields */}
          <div className="space-y-4 mb-5">
            {formType === "login" && (
              <LoginForm 
                errors={errors} 
                fieldFocus={fieldFocus}
                setFieldFocus={setFieldFocus}
                colors={colors}
              />
            )}
            {formType === "register" && (
              <RegisterForm 
                errors={errors}
                fieldFocus={fieldFocus}
                setFieldFocus={setFieldFocus}
                colors={colors}
              />
            )}
            {formType === "recover" && (
              <RecoverForm 
                errors={errors}
                fieldFocus={fieldFocus}
                setFieldFocus={setFieldFocus}
                colors={colors}
              />
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-3.5 sm:py-4 rounded-xl font-semibold text-white transition-all relative overflow-hidden disabled:opacity-70 disabled:cursor-not-allowed hover:opacity-90 active:scale-[0.98]"
            style={{ 
              backgroundColor: colors.primary,
              boxShadow: `0 4px 12px ${colors.primary}40`,
            }}
          >
            <div 
              className="absolute inset-0 opacity-30"
              style={{ backgroundColor: colors.success }}
            />
            <span className="relative z-10">
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin">⏳</span>
                  {formType === "login" && "Signing in..."}
                  {formType === "register" && "Creating account..."}
                  {formType === "recover" && "Sending..."}
                </span>
              ) : (
                <>
                  {formType === "login" && "Sign in"}
                  {formType === "register" && "Create account"}
                  {formType === "recover" && "Send Verification Code"}
                </>
              )}
            </span>
          </button>

          {/* Footer Links */}
          <div className="mt-6 text-center space-y-3">
            {formType === "login" && (
              <button
                type="button"
                onClick={() => setFormType("recover")}
                className="text-sm font-semibold hover:underline"
                style={{ color: colors.primary }}
              >
                Forgot password?
              </button>
            )}
            
            {formType === "recover" && (
              <button
                type="button"
                onClick={() => setFormType("login")}
                className="text-sm font-semibold hover:underline flex items-center justify-center gap-2 mx-auto"
                style={{ color: colors.primary }}
              >
                <ArrowBackIcon size={16} color={colors.primary} />
                Back to Sign In
              </button>
            )}

            {/* OAuth Buttons */}
            {formType !== "recover" && (
              <div className="mt-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex-1 h-px" style={{ backgroundColor: colors.border }} />
                  <span className="text-xs sm:text-sm" style={{ color: colors.secondary }}>or</span>
                  <div className="flex-1 h-px" style={{ backgroundColor: colors.border }} />
                </div>
                <div className="flex flex-col sm:flex-row justify-center gap-3">
                  {AUTH_METHODS.OPTIONS.filter(
                    (option) => option.provider !== "email"
                  ).map((option) => (
                    <button
                      key={option.provider}
                      type="button"
                      onClick={() => handleProviderClick(option.authUrl)}
                      className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border transition-all hover:opacity-80 active:scale-[0.98]"
                      style={{
                        borderColor: colors.border,
                        backgroundColor: colors.card,
                        color: colors.text,
                        minWidth: '140px',
                      }}
                    >
                      {option.provider === "google" && (
                        <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                      )}
                      <span className="text-sm font-medium whitespace-nowrap">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </form>
        </div>
      </div>
    </div>
  );

  // Use React Portal to render modal at document body level
  return ReactDOM.createPortal(
    modalContent,
    document.body
  );
};

export default EmailLoginDialog;
